import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CertificationContext } from 'contexts/certifications';
import { SessionContext } from 'contexts/session';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SortableHeader } from 'components/Table';
import {
  fromOrdering,
  getColumnDirection,
  getNextSortState,
  saveSortState,
  toOrdering,
} from 'utils/sorting';
import {
  CertificationsContainer,
  ColumnContainer,
  ColumnNameIcon,
  Container,
  HeaderRow,
  PaginationContainer,
  TableScrollWrapper,
  Title,
} from './styles';
import { Table, User } from 'assets/styles/styles';
import CertificationsFilters from '../CertificationsFillters/CertificationsFilters';
import AssignVoucherModal from '../AssignVoucherModal';
import { capitalize, formatDate, formatScore } from 'utils/helpers';
import { Spinner } from 'components/loadings';
import { Pagination } from 'components/Table';
import Tooltip from 'components/tooltip';
import { certifications } from 'constants/certifications';
import { INE_CONTACT_SALES } from 'constants/settings';
import ButtonIcon from 'components/buttonIcon';
import Button from 'components/button';
import Icon from 'components/icons';

const InfoIconTooltip = ({ title }: { title: string }) => (
  <Tooltip title={title} placement="right" overlayInnerStyle={{ textAlign: 'center' }}>
    <span>
      <Icon icon="Info" fill="var(--lilac)" size="small" />
    </span>
  </Tooltip>
);

const UserDetailsCell = ({ name, email }: { name: string; email: string }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const emailRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncatedState = useCallback(() => {
    const nameElement = nameRef.current;
    const emailElement = emailRef.current;

    setIsTruncated(
      Boolean(
        (nameElement && nameElement.scrollWidth > nameElement.clientWidth) ||
          (emailElement && emailElement.scrollWidth > emailElement.clientWidth)
      )
    );
  }, []);

  useEffect(() => {
    const nameElement = nameRef.current;
    const emailElement = emailRef.current;
    if (!nameElement || !emailElement) return;

    updateTruncatedState();

    const observer = new ResizeObserver(updateTruncatedState);
    observer.observe(nameElement);
    observer.observe(emailElement);

    return () => observer.disconnect();
  }, [name, email, updateTruncatedState]);

  const userDetails = (
    <User.Container>
      <User.Name ref={nameRef}>{name}</User.Name>
      <User.Email ref={emailRef}>{email}</User.Email>
    </User.Container>
  );

  if (!isTruncated) return userDetails;

  return (
    <Tooltip
      placement="bottomLeft"
      title={
        <>
          <div>{name}</div>
          <div>{email}</div>
        </>
      }
    >
      {userDetails}
    </Tooltip>
  );
};

interface CertificationTableProps {
  noMobile?: boolean;
  isFetching: boolean;
}

const CertificationTable: React.FC<CertificationTableProps> = ({
  noMobile = false,
  isFetching,
}) => {
  const { actions, filters, usersTable, usersCount, voucherSummary } =
    useContext(CertificationContext);

  const { account, isPastDue } = useContext(SessionContext);

  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;
  const dashboardCertificationsSorting = useFlags()?.dashboardCertificationsSorting ?? false;

  useEffect(() => {
    if (!dashboardCertificationsSorting && filters?.ordering) {
      actions.onChangeFilters({ ...filters, ordering: null, page: 1 });
    }
  }, [dashboardCertificationsSorting]);

  const userColumnMinWidth = 160;
  const userColumnMaxWidth = 260;
  const userColumnWidth = useMemo(() => {
    const longestUserTextLength = usersTable?.reduce((maxLength, user) => {
      return Math.max(maxLength, user?.user_name?.length ?? 0, user?.user_email?.length ?? 0);
    }, 'User'.length);

    return Math.min(
      userColumnMaxWidth,
      Math.max(userColumnMinWidth, (longestUserTextLength ?? userColumnMinWidth) * 8)
    );
  }, [usersTable]);

  const tableColumns = `${userColumnWidth}px minmax(0, 1.45fr) minmax(0, 1.45fr) minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.35fr) 64px`;
  const withEmptySymbol = true;

  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const updateScrollShadows = useCallback(() => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    const { clientWidth, scrollLeft, scrollWidth } = el;
    el.dataset.shadowLeft = scrollLeft > 0 ? 'true' : '';
    el.dataset.shadowRight = scrollLeft + clientWidth < scrollWidth ? 'true' : '';
  }, []);

  useEffect(() => {
    requestAnimationFrame(updateScrollShadows);
  }, [usersTable, updateScrollShadows]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollShadows);
    return () => window.removeEventListener('resize', updateScrollShadows);
  }, [updateScrollShadows]);

  const handleCertificationLink = (url: string) => () => {
    window.open(url, '_blank');
  };
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const sortState = fromOrdering(filters?.ordering);

  const handleSortToggle = (columnKey: string) => {
    const nextState = getNextSortState(sortState, columnKey);
    const nextOrdering = toOrdering(nextState);
    saveSortState({ table: 'certifications', context: account?.id }, nextState);
    actions.onChangeFilters({
      ...filters,
      ordering: nextOrdering || null,
      page: 1,
    });
  };

  const displayCertification = (certification: string): string => {
    const cert = Object.values(certifications).find((cert) => cert.short_name === certification);
    return cert?.name;
  };

  const handlePaginationChange = (key, value) => {
    const reset_page = key === 'page_size' ? { page: 1 } : {};
    actions.onChangeFilters({ ...filters, [key]: Number(value), ...reset_page });
  };

  const currentAttempt = (attempts, pos) => {
    const attemptLength = attempts?.length;
    if (!attemptLength) return null;
    if (attemptLength === 2) return pos === 1 ? attempts[1] : attempts[0];
    return pos === 1 ? attempts[0] : null;
  };

  const normalizeStatus = (attempt) => {
    return attempt?.status?.toLowerCase().replace(/\s+/g, '_') ?? '';
  };

  const formatAttemptResult = (attempts, pos): JSX.Element => {
    const attempt = currentAttempt(attempts, pos);
    const noData = <>-</>;
    if (!attempt) {
      if (pos === 2) {
        const firstAttemptPassed = normalizeStatus(attempts[0]) === 'passed';
        if (firstAttemptPassed) return <>N/A</>;
      }
      return noData;
    }

    const normalizedStatus = normalizeStatus(attempt);
    const isAttemptTimedOut = normalizedStatus === 'timed_out';
    const isScoreNull = attempt.score === null;

    if (!isAttemptTimedOut && isScoreNull) return noData;

    const scoreRow = isAttemptTimedOut ? 'Time Expired' : formatScore(attempt.score);
    const labelRow = isAttemptTimedOut ? 'Failed' : capitalize(attempt.status);

    return (
      <ColumnContainer>
        <div>{scoreRow}</div>
        <div style={{ color: labelRow === 'Passed' ? 'var(--positive-dark)' : undefined }}>
          {labelRow}
        </div>
      </ColumnContainer>
    );
  };

  const formatExpirationDate = (attempt): JSX.Element => {
    const { expiration_date, is_expired, expires_soon, days_to_expire } = attempt;
    return (
      <ColumnContainer>
        <div>{formatDate(expiration_date, withEmptySymbol)}</div>
        {is_expired && <div style={{ color: 'var(--bright-gray)' }}>Expired</div>}
        {expires_soon && (
          <div style={{ color: 'var(--egg-orange)' }}>{expiringLabel(days_to_expire)}</div>
        )}
      </ColumnContainer>
    );
  };

  const expiringLabel = (days_to_expire: number): string => {
    if (days_to_expire === 0) return 'Expired today';
    return `Expiring in ${days_to_expire} ${days_to_expire > 1 ? 'days' : 'day'}`;
  };

  const hasVouchers = useMemo(() => {
    return (voucherSummary?.voucher_balance?.remaining ?? 0) > 0;
  }, [voucherSummary]);

  const canAssignVouchers = useMemo(() => {
    return hasVouchers && !isPastDue;
  }, [hasVouchers, isPastDue]);

  const handleAssignVoucher = () => {
    setAssignModalOpen(true);
  };

  const handleContactSales = () => {
    const subject = encodeURIComponent('Request for Additional Certification Vouchers');
    const body = encodeURIComponent(
      'Hi,\n\nI would like to request additional certification vouchers for my account.\n\nThank you'
    );
    const salesEmail = account?.sales_contact?.email;
    const mailtoUrl = salesEmail
      ? `mailto:${INE_CONTACT_SALES},${salesEmail}?subject=${subject}&body=${body}`
      : `mailto:${INE_CONTACT_SALES}?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
  };

  const handleCtaClick = () => {
    if (isPastDue) return;
    canAssignVouchers ? handleAssignVoucher() : handleContactSales();
  };

  const tableHasData = useMemo(() => usersTable?.length > 0, [usersTable]);

  const tableHeight = useMemo(() => {
    const minHeigth = 390;
    if (!usersTable || isFetching) return `${minHeigth}px`;
    const userTableHeight = 130 + usersTable.length * 54;
    return `${Math.max(userTableHeight, minHeigth)}px`;
  }, [usersTable, isFetching]);

  const expireSoonRows = useMemo(() => {
    const rows = usersTable?.filter((row) => row?.expires_soon)?.length ?? 0;
    return Math.min(rows, filters.page_size);
  }, [usersTable, filters.page_size]);

  const expireSoon = (index: number, expires_soon: boolean) => {
    const currentIndex = index + 1;
    if (!expires_soon || currentIndex > expireSoonRows) return null;
    return {
      firstRow: currentIndex === 1,
      middleRow: currentIndex > 1 && currentIndex < expireSoonRows,
      lastRow: currentIndex === expireSoonRows,
    };
  };

  const canExtendVoucher = (expiresSoon: boolean, attempts?: unknown[]): boolean =>
    expiresSoon && (!attempts || attempts.length === 0);

  return (
    <Container noMobile={noMobile}>
      <HeaderRow>
        <Title>Assigned vouchers</Title>
        {voucherAssignmentV1 && (
          <Button onClick={handleCtaClick} disabled={isPastDue}>
            {hasVouchers ? 'Assign Vouchers' : 'Contact sales to add vouchers'}
          </Button>
        )}
      </HeaderRow>
      <CertificationsContainer>
        <CertificationsFilters />
        <Table.Wrapper
          style={{
            width: '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            height: tableHeight,
          }}
        >
          {isFetching && (
            <Table.LoadingWrapper>
              <div>
                <Spinner active={isFetching} size="50" />
              </div>
            </Table.LoadingWrapper>
          )}
          {!isFetching && tableHasData && (
            <>
              <TableScrollWrapper ref={scrollWrapperRef} onScroll={updateScrollShadows}>
                <Table.Container style={{ minWidth: '1400px' }}>
                  <Table.Thead>
                    <Table.Tr borderBottom={true} columns={tableColumns}>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="User"
                            columnKey="name"
                            direction={getColumnDirection(sortState, 'name')}
                            onToggle={handleSortToggle}
                            tooltip="User name"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>User</ColumnNameIcon>
                            <InfoIconTooltip title="User name" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="Certification"
                            columnKey="certification"
                            direction={getColumnDirection(sortState, 'certification')}
                            onToggle={handleSortToggle}
                            tooltip="Certification short name"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>Certification</ColumnNameIcon>
                            <InfoIconTooltip title="Certification short name" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="Assigned date"
                            columnKey="assigned_date"
                            direction={getColumnDirection(sortState, 'assigned_date')}
                            onToggle={handleSortToggle}
                            tooltip="Date voucher was assigned to user"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>Assigned date</ColumnNameIcon>
                            <InfoIconTooltip title="Date voucher was assigned to user" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="Expiration date"
                            columnKey="expiration_date"
                            direction={getColumnDirection(sortState, 'expiration_date')}
                            onToggle={handleSortToggle}
                            tooltip="Voucher expiration date"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>Expiration date</ColumnNameIcon>
                            <InfoIconTooltip title="Voucher expiration date" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="Attempt 1"
                            columnKey="attempt_1"
                            direction={getColumnDirection(sortState, 'attempt_1')}
                            onToggle={handleSortToggle}
                            tooltip="First exam attempt results"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>Attempt 1</ColumnNameIcon>
                            <InfoIconTooltip title="First exam attempt results" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        {dashboardCertificationsSorting ? (
                          <SortableHeader
                            label="Attempt 2"
                            columnKey="attempt_2"
                            direction={getColumnDirection(sortState, 'attempt_2')}
                            onToggle={handleSortToggle}
                            tooltip="Second exam attempt results: Must be completed within 14 days of the first attempt"
                          />
                        ) : (
                          <>
                            <ColumnNameIcon>Attempt 2</ColumnNameIcon>
                            <InfoIconTooltip title="Second exam attempt results: Must be completed within 14 days of the first attempt" />
                          </>
                        )}
                      </Table.Th>
                      <Table.Th>
                        <SortableHeader
                          label="Awarded date"
                          columnKey="awarded_date"
                          direction={getColumnDirection(sortState, 'awarded_date')}
                          onToggle={handleSortToggle}
                          tooltip="Date certification was awarded"
                        />
                      </Table.Th>
                      <Table.Th aria-label="Actions" />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody height="54px">
                    {!isFetching &&
                      usersTable?.length > 0 &&
                      usersTable?.map((report, index) => {
                        const {
                          id,
                          user_name,
                          user_email,
                          certification,
                          assigned_date,
                          expiration_date,
                          awarded_date,
                          accredible_url,
                          attempts,
                          is_expired,
                          expires_soon,
                          days_to_expire,
                        } = report;

                        return (
                          <Table.Tr
                            borderBottom={true}
                            key={`${index}-${id}`}
                            columns={tableColumns}
                            expireSoon={expireSoon(index, expires_soon)}
                          >
                            <Table.Td>
                              <UserDetailsCell name={user_name} email={user_email} />
                            </Table.Td>
                            <Table.Td withEllipsis={true}>
                              <Tooltip title={displayCertification(certification)}>
                                {certification}
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>{formatDate(assigned_date, withEmptySymbol)}</Table.Td>
                            <Table.Td>
                              {formatExpirationDate({
                                expiration_date,
                                is_expired,
                                days_to_expire,
                                expires_soon,
                              })}
                            </Table.Td>
                            <Table.Td>{formatAttemptResult(attempts, 1)}</Table.Td>
                            <Table.Td>{formatAttemptResult(attempts, 2)}</Table.Td>
                            <Table.Td>{formatDate(awarded_date, withEmptySymbol)}</Table.Td>
                            <Table.Td
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '36px',
                                justifyContent: 'center',
                              }}
                            >
                              {canExtendVoucher(expires_soon, attempts) && (
                                <Tooltip placement="top" title={'Contact INE to extend'}>
                                  <ButtonIcon
                                    data-testid="contact-ine-button"
                                    $secondary
                                    size={'small'}
                                    icon={'Clock'}
                                    onClick={() => window.open('mailto:sales@ine.com', '_blank')}
                                  />
                                </Tooltip>
                              )}
                              {accredible_url && (
                                <Tooltip placement="bottomRight" title={'See certification'}>
                                  <ButtonIcon
                                    $secondary
                                    size={'small'}
                                    icon={'ExternalLink'}
                                    onClick={handleCertificationLink(accredible_url)}
                                  />
                                </Tooltip>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                  </Table.Tbody>
                </Table.Container>
              </TableScrollWrapper>
              <PaginationContainer>
                <Pagination
                  count={usersCount}
                  page_size={filters.page_size}
                  page={filters.page}
                  rowsPerPageList={['10', '20', '30', '50']}
                  onPagesClick={(value, name) => handlePaginationChange(name, value)}
                />
              </PaginationContainer>
            </>
          )}
          {!isFetching && !tableHasData && (
            <Table.EmptyState>
              <Table.EmptyStateTitle>Sorry, we couldn’t find any matches</Table.EmptyStateTitle>
              <Table.EmptyStateText>Please adjust filters and try again </Table.EmptyStateText>
            </Table.EmptyState>
          )}
        </Table.Wrapper>
      </CertificationsContainer>

      {voucherAssignmentV1 && (
        <AssignVoucherModal
          visible={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          handleContactSales={handleContactSales}
        />
      )}
    </Container>
  );
};

export default CertificationTable;
